/* Sign-in page. The app renders nothing else until a session exists.
 * Deliberately single-theme: a committed white homepage (see login.css) —
 * the dark/light choice applies inside the applications, not here. */
import { useState, type FormEvent } from 'react'
import { login, MfaChallengeError, verifyMfa, type SessionUser } from '@/lib/auth'
import './login.css'

interface Props {
  title: string
  subtitle: string
  hint?: string
  onSuccess: (user: SessionUser) => void
  onExpired: () => void
}

export function Login({ title, subtitle, hint, onSuccess, onExpired }: Props) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [org, setOrg] = useState('DEMO')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Set once the password has been accepted for an MFA-enabled account: the
  // form then asks for the authenticator code instead of throwing the token
  // away, which is what made every MFA-enrolled operator unable to sign in.
  const [challenge, setChallenge] = useState<string | null>(null)
  const [code, setCode] = useState('')

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      if (challenge) {
        onSuccess(await verifyMfa(challenge, code, email.trim(), onExpired))
      } else {
        onSuccess(await login(email.trim(), password, org.trim().toUpperCase(), onExpired))
      }
    } catch (err) {
      if (err instanceof MfaChallengeError) {
        setChallenge(err.challengeToken)
        setCode('')
      } else {
        setError(err instanceof Error ? err.message : 'sign-in failed')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="qx-login">
      <aside className="qx-login-brand">
        <div className="qx-login-logo">
          <img src="/qorvex.png" alt="Qorvex — Algo, Quant and HFT Trading" />
        </div>

        <div className="qx-login-hero">
          <h1>Execution you can audit. Risk you cannot bypass.</h1>
          <p>
            An institutional trading platform for NSE, BSE, NASDAQ and NYSE —
            every order passes the risk gate, every fill is recorded, and
            backtest, paper and live results are never mixed.
          </p>
        </div>

        <div className="qx-login-points">
          <div><b>RISK GATE</b><span>no compilable path to a venue skips the pre-trade checks</span></div>
          <div><b>4 VENUES</b><span>India first — NSE &amp; BSE — with NASDAQ and NYSE alongside</span></div>
          <div><b>MEASURED</b><span>latency figures are benchmarked percentiles, never claims</span></div>
        </div>

        <svg className="qx-login-spark" viewBox="0 0 600 200" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="qx-fade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#2f6fd6" stopOpacity="0.14" />
              <stop offset="1" stopColor="#2f6fd6" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,150 L40,140 L80,155 L120,120 L160,132 L200,98 L240,110 L280,84
                   L320,96 L360,70 L400,86 L440,58 L480,72 L520,44 L560,60 L600,36
                   L600,200 L0,200 Z" fill="url(#qx-fade)" />
          <path d="M0,150 L40,140 L80,155 L120,120 L160,132 L200,98 L240,110 L280,84
                   L320,96 L360,70 L400,86 L440,58 L480,72 L520,44 L560,60 L600,36"
                fill="none" stroke="#7fa4e0" strokeWidth="1.6" />
        </svg>
      </aside>

      <main className="qx-login-side">
        <form className="qx-login-card" onSubmit={submit}>
          <div>
            <h2>{title}</h2>
            <div className="sub">{subtitle}</div>
          </div>

          {challenge ? (
            <div className="qx-field">
              <label htmlFor="qx-code">Authenticator code</label>
              <div className="control">
                <input id="qx-code" className="mono" inputMode="numeric" autoFocus
                       autoComplete="one-time-code" pattern="[0-9]{6,10}" required
                       placeholder="6-digit code" value={code}
                       onChange={(e) => setCode(e.target.value.replace(/\s+/g, ''))} />
              </div>
              <div className="sub">
                Signed in as <b>{email}</b>. Enter the code from your authenticator app
                to finish signing in.{' '}
                <button type="button" className="qx-reveal" style={{ position: 'static', transform: 'none' }}
                        onClick={() => { setChallenge(null); setCode(''); setError(null) }}>
                  USE A DIFFERENT ACCOUNT
                </button>
              </div>
            </div>
          ) : (
            <>
            <div className="qx-field">
              <label htmlFor="qx-email">Email</label>
              <div className="control">
                <input id="qx-email" type="email" autoComplete="username" autoFocus
                       required placeholder="you@yourfirm.com" value={email}
                       onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>

            <div className="qx-field">
              <label htmlFor="qx-password">Password</label>
              <div className="control">
                <input id="qx-password" type={showPw ? 'text' : 'password'}
                       autoComplete="current-password" required
                       placeholder="••••••••••••" value={password}
                       style={{ paddingRight: 56 }}
                       onChange={(e) => setPassword(e.target.value)} />
                <button type="button" className="qx-reveal"
                        onClick={() => setShowPw((v) => !v)}
                        aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? 'HIDE' : 'SHOW'}
                </button>
              </div>
            </div>

            <div className="qx-field">
              <label htmlFor="qx-org">Organization</label>
              <div className="control">
                <input id="qx-org" className="mono" value={org}
                       onChange={(e) => setOrg(e.target.value)} />
              </div>
            </div>
            </>
          )}

          {error && (
            <div className="qx-login-error" role="alert">
              <span aria-hidden="true">⚠</span>
              <span>{error}</span>
            </div>
          )}

          <button type="submit" className="qx-login-submit" disabled={busy}>
            {busy ? (challenge ? 'Verifying…' : 'Signing in…') : (challenge ? 'Verify code' : 'Sign in')}
          </button>

          {hint && <div className="qx-login-hint">{hint}</div>}

          <div className="qx-login-foot">
            © IDA Tech-Solutions LLP · regulated markets only · no performance guarantees
          </div>
        </form>
      </main>
    </div>
  )
}
